import getPersonaPhoto from '../actions/getPersonaPhoto';
import getHexConsumerIdForUser from '../actions/getHexConsumerIdForUser';
import PersonaControlViewState from '../store/schema/PersonaControlViewState';
import personaControlStore from '../store/Store';
import getPersonaControlKey from '../utils/getPersonaControlKey';
import { observer } from 'mobx-react';
import * as React from 'react';
import {
    Persona,
    PersonaPresence,
    PersonaSize,
    PersonaInitialsColor,
    IPersonaStyles,
} from 'office-ui-fabric-react/lib/Persona';
import { IPersonaProps, IPersonaCoinProps } from 'office-ui-fabric-react/lib/components/Persona';
import { IRenderFunction } from 'office-ui-fabric-react/lib/Utilities';
import { isFeatureEnabled } from 'owa-feature-flags';

const styles = require('./PersonaControl.scss');
let classNames = require('classnames/bind').bind(styles);

export interface PersonaControlProps {
    emailAddress: string;
    name: string;
    size: PersonaSize;
    className?: string;
    showPersonaDetails?: boolean;
    statusText?: string;
    secondaryText?: string;
    showSecondaryText?: boolean;
    showPresence?: boolean;
    presence?: PersonaPresence;
    hexConsumerIdForUser?: string;
    onRenderPrimaryText?: IRenderFunction<IPersonaProps>;
    onRenderInitials?: IRenderFunction<IPersonaProps>;
    onRenderCoin?: IRenderFunction<IPersonaProps>;
    personaId?: string;
    initialsColor?: PersonaInitialsColor;
    showUnknownPersonaCoin?: boolean;
    mailboxType: string;
    styleProps?: Partial<IPersonaStyles>;
    coinProps?: Partial<IPersonaCoinProps>;
    skipLoadingPhoto?: () => boolean;
}

@observer
export default class PersonaControl extends React.Component<PersonaControlProps, any> {
    public static defaultProps: PersonaControlProps = {
        emailAddress: '',
        name: '',
        size: PersonaSize.regular,
        secondaryText: '',
        showPresence: false,
        mailboxType: '',
    };

    componentDidMount() {
        this.fetchPersonaPhoto(this.props);
    }

    componentDidUpdate(prevProps: PersonaControlProps) {
        const currentKey = getPersonaControlKey(
            this.props.emailAddress,
            this.props.personaId,
            this.props.size,
            this.props.mailboxType,
            this.props.name
        );

        const previousKey = getPersonaControlKey(
            prevProps.emailAddress,
            prevProps.personaId,
            prevProps.size,
            prevProps.mailboxType,
            prevProps.name
        );

        if (previousKey !== currentKey) {
            this.fetchPersonaPhoto(this.props);
        }
    }

    fetchPersonaPhoto(props: PersonaControlProps) {
        if (props.emailAddress || props.name || props.personaId) {
            getPersonaPhoto(
                props.name,
                props.emailAddress,
                props.personaId,
                props.size,
                props.hexConsumerIdForUser || getHexConsumerIdForUser(props.emailAddress),
                props.mailboxType,
                props.skipLoadingPhoto
            );
        }
    }

    renderPersona(viewState: PersonaControlViewState) {
        let {
            className,
            size,
            name,
            statusText,
            showPersonaDetails,
            secondaryText,
            showSecondaryText,
            presence,
            onRenderPrimaryText,
            onRenderCoin,
            initialsColor,
            onRenderInitials,
            styleProps,
            coinProps,
        } = this.props;

        let photoDisplayProps: any = {};
        if (viewState.personaBlob && viewState.personaBlob.blobUrl) {
            photoDisplayProps.imageUrl = viewState.personaBlob.blobUrl;
            photoDisplayProps.imageShouldFadeIn = false;
            photoDisplayProps.imageShouldStartVisible = true;
        }

        const showUnknownPersonaCoin =
            isFeatureEnabled('peo-unauthenticatedSender') && !!this.props.showUnknownPersonaCoin;

        return (
            <Persona
                className={classNames(className, styles.persona)}
                presence={presence || PersonaPresence.none}
                hidePersonaDetails={!showPersonaDetails}
                size={size}
                text={name}
                secondaryText={secondaryText}
                showSecondaryText={showSecondaryText}
                tertiaryText={statusText}
                onRenderPrimaryText={onRenderPrimaryText}
                onRenderCoin={onRenderCoin}
                onRenderInitials={onRenderInitials}
                {...photoDisplayProps}
                initialsColor={initialsColor}
                showUnknownPersonaCoin={showUnknownPersonaCoin}
                styles={styleProps}
                coinProps={coinProps}
            />
        );
    }

    render() {
        let personaControlKey = getPersonaControlKey(
            this.props.emailAddress,
            this.props.personaId,
            this.props.size,
            this.props.mailboxType,
            this.props.name
        );
        let viewState = personaControlStore.viewStates.get(personaControlKey);

        if (viewState) {
            return this.renderPersona(viewState);
        } else {
            return null;
        }
    }
}
